import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { DateInRangesValidator } from "@cervval.js/common/src/services/validation.impl/validators/dateEditor.validators";
import { validatorEntry } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { DEFAULT_DATE_FORMAT, LooseParsingOptions, UnixTimestamp, UnixTimestampIndeterminateInterval, beginOfNext, extractLooseParsingOptions, format, fromDateObject, isValid, parse, subtract } from "@cervval.js/common/src/tools/timestamps.tools";
import { CjsDatePicker, DatePickerOptions } from "../../../pickers/datePicker/datePicker";
import { EditorWithPicker, EditorWithPickerOptions, defaultEditorWithPickerOptions } from "../editorWithPicker";

export interface DateEditorOptions extends EditorWithPickerOptions, LooseParsingOptions {

    /**
     * Editor is editing a finishDate
     * Will store beginOfNextDay, but keeps displaying picked date
     * Ex:
     * [27/02/2021] displayed in editor
     * value: 28/02/2021 00:00:00 UTC
     */
    isFinishDate?: boolean;

    /**
     * If set the input won't be accepted if year is lower than minYear
     */
    minYear?: number;

    /**
     * If set the input won't be accepted if year is greater than maxYear (default is 9999)
     */
    maxYear?: number;

}

export const defaultDateEditorOptions: DateEditorOptions = {
    ...defaultEditorWithPickerOptions,
    popupMode: "direct",
    buttonIcon: { name: "fa-calendar-days" },
    maxYear: 9999
};

@CustomElement({
    name: "cjs-date-editor",
    elementCssClasses: ["cjs-editor"]
})
export class CjsDateEditor extends EditorWithPicker<UnixTimestamp, void, CjsDatePicker> {

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultDateEditorOptions)
    public readonly options!: WebComponentOptions<DateEditorOptions>;

    /** Shortcut to date picker options */
    public get pickerOptions(): WebComponentOptions<DatePickerOptions> {
        return this._picker.options;
    }

    /**
     * The DatePicker used to edit the value of the input
     */
    protected _picker = new CjsDatePicker();

    constructor(options?: Partial<DateEditorOptions>, datePickerOptions?: Partial<DatePickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.pickerOptions.setWithoutRefresh(datePickerOptions);
    }

    /**
     * Instantiate picker on first call, and refresh picker based on value and options (called before showPopup)
     */
    protected _refreshPicker(): CjsDatePicker {
        if (this._value != null) {
            // Handle finishDate propertyConfig, remove one day from value to display correct value
            this._picker.setValue(this.options.isFinishDate ? subtract(this._value, { day: 1 }).uts : this._value);
            this._picker.refresh();
        } else {// Undefined, reset picker // TODO Create and replace with empty picker
            this._picker = new CjsDatePicker(this.pickerOptions);
        }
        return this._picker;
    }

    /**
     * Format the current date to a string which can be display for Humans
     */
    public convertValueToInputValue(ts: UnixTimestamp | undefined): string {
        if (ts && isValid(ts)) {
            let tsToFormat = ts;
            if (this.options.isFinishDate) {// Handle finishDate propertyConfig, remove one day from value to display correct value
                tsToFormat = subtract(ts, { day: 1 }).uts;
            }
            return format(tsToFormat, DEFAULT_DATE_FORMAT);
        } else {
            return "";
        }
    }

    /**
     * Get the value of the component from the string in the input
     */
    protected _convertInputValueToValue(inputValue: string | undefined): UnixTimestamp | undefined {
        if (inputValue == null || inputValue === "") {
            return undefined;
        }

        const inputTsDate = parse(inputValue, DEFAULT_DATE_FORMAT, extractLooseParsingOptions(this.options));

        if (isValid(inputTsDate.uts)) {
            return this.options.isFinishDate ? beginOfNext(inputTsDate, "day").uts : inputTsDate.uts;
        } else {
            return undefined;
        }
    }

    /**
     * Decorate the input depending on input value validity
     * @returns false if value is invalid
     */
    protected override _validateInputValue(value: UnixTimestamp | undefined): boolean {
        if (!super._validateInputValue(value)) {
            return false;
        }
        if (value != null && !isValid(value)) {
            const inputString = this.getInput().input.value;
            this.setValidationError(this._validation.getDefaultValidationError(inputString ?? ""));
            return false;
        }
        return this._validateRanges(value);
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): UnixTimestamp | undefined {
        return this._convertInputValueToValue(this.getInput().input.value);
    }

    /**
     * @returns the picker value
     */
    protected override _getPickerValue(): UnixTimestamp | undefined {
        const pickerValue = this._picker?.getValue();

        if (pickerValue === undefined) { return undefined; }

        return this.options.isFinishDate ? beginOfNext(pickerValue, "day").uts : pickerValue;
    }

    /** Validate value in enabled ranges of dateEditor */
    protected _validateRanges(value?: UnixTimestamp): boolean {
        if (this.pickerOptions.ranges && !this._validateRangesImpl(value, this.pickerOptions.ranges)) {
            return false;
        }

        // add range corresponding to min and max year options
        if (this.options.minYear || this.options.maxYear) {
            const range = {
                start: this.options.minYear ? fromDateObject({ year: this.options.minYear }).uts : undefined,
                finish: this.options.maxYear ? fromDateObject({ year: this.options.maxYear }).add({ year: 1, second: -1 }).uts : undefined
            };
            if (!this._validateRangesImpl(value, [range])) { return false; }
        }
        return true;
    }

    /** Validate value with provided ranges (date must be valid for at least one range */
    protected _validateRangesImpl(value: UnixTimestamp | undefined, ranges: UnixTimestampIndeterminateInterval[]): boolean {
        const rangesPropertyConfig: PropertyConfig = {
            propertyName: "ranges",
            label: "",
            typeUid: PropertyConfigType.DATE,
            validators: {
                ...validatorEntry(DateInRangesValidator, { ranges })
            }
        };
        const propsValues: Record<string, unknown> = {
            "ranges": value ?? this._value
        };
        const validatedProps: Record<string, PropertyConfig> = {
            "ranges": rangesPropertyConfig
        };

        const errors = this._validation.validate(validatedProps, propsValues);

        this.setValidationError(errors["ranges"]);

        // Check if no error given === valid time
        return Object.keys(errors).length === 0;
    }
}
